import React from 'react';
import { 
  AllieItem, 
  AllieCard, 
  AllieText, 
  AllieAlertCircleOutlineIcon,
  AllieButton
} from '../../..';
import { 
  ZapIcon,
  ButtonContainer,
  StyledBanner,
  StyledApplyButton
} from './DuplicationBanner.styles';

export interface AllieDuplicationBannerProps {
  /** Función que se ejecuta cuando se aplican los cambios */
  onApply: () => void;
  /** Función que se ejecuta cuando se cancela la acción */
  onCancel?: () => void;
  /** Título del banner que describe la acción */
  title: string;
  /** Texto del botón de aplicar */
  applyText?: string;
  /** Texto del botón de cancelar */
  cancelText?: string;
  /** Deshabilita el botón de aplicar */
  disabledApply?: boolean;
  /** Deshabilita el botón de cancelar */
  disabledCancel?: boolean;
  /** Indica si está en estado de carga */
  loading?: boolean;
}

/**
 * Banner genérico de duplicación que permite al usuario aplicar
 * la misma configuración a múltiples elementos seleccionados.
 * 
 * @example
 * ```tsx
 * <AllieDuplicationBanner
 *   title="Selecciona los usuarios que deseas aplicar la misma configuración que Carlos Rodríguez"
 *   applyText="Aplicar cambios"
 *   cancelText="Cancelar"
 *   onApply={handleApply}
 *   onCancel={handleCancel}
 *   disabledApply={!hasSelection}
 * />
 * ```
 */
export const AllieDuplicationBanner: React.FC<AllieDuplicationBannerProps> = ({
  onApply,
  onCancel,
  title,
  applyText = 'Aplicar',
  cancelText = 'Cancelar',
  disabledApply = false,
  disabledCancel = false,
  loading = false,
}) => {
  return (
    <StyledBanner>
      <AllieCard
        style={{
          borderRadius: 'var(--allie-radius-xl)',
          margin: '4px 0 8px',
          width: '99%',
        }}
        content={{
          style: {
            padding: 0,
            height: '100%',
          },
          children: (
            <AllieItem>
              <ZapIcon slot="start">
                <AllieAlertCircleOutlineIcon 
                  size="16" 
                  allieColor="white" 
                  color="white" 
                />
              </ZapIcon>
              <AllieText
                color="neutral-60" 
                weight={600}
                style={{ flex: 1 }}
              >
                {title}
              </AllieText>
              <ButtonContainer slot="end">
                {onCancel && (
                  <StyledApplyButton
                    corner="rounded-edges-plus"
                    onClick={onCancel}
                    size="small"
                    allieType="secondary"
                    disabled={disabledCancel || loading}
                    style={{
                      padding: 'var(--allie-spacing-md)',
                      borderRadius: 'var(--allie-radius-md)',
                    }}
                  >
                    <AllieText color="neutral-90" weight={500}>
                      {cancelText}
                    </AllieText>
                  </StyledApplyButton>
                )}
                <StyledApplyButton
                  corner="rounded-edges-plus"
                  onClick={onApply}
                  size="small"
                  disabled={disabledApply || loading}
                  loading={loading}
                  style={{
                    padding: 'var(--allie-spacing-md)',
                    borderRadius: 'var(--allie-radius-md)',
                  }}
                >
                  <AllieText color="white" weight={600}>
                    {applyText}
                  </AllieText>
                </StyledApplyButton>
              </ButtonContainer>
            </AllieItem>
          ),
        }}
      />
    </StyledBanner>
  );
};

AllieDuplicationBanner.displayName = 'AllieDuplicationBanner';
